import { run } from "effection";
import { uniq } from "lodash";
import compose from "lodash/fp/compose";
import isNull from "lodash/fp/isNull";
import isUndefined from "lodash/fp/isUndefined";
import split from "lodash/fp/split";

import { commonStyle } from "@app/style/commonForms";

import type { TCurrency } from "@app/types";
import type { Operation } from "effection";

export const invisibleItemOpts = {
  location: "before",
  widget: "dxButton",
  options: {
    elementAttr: {
      id: "invisiblebutton",
    },
  },
};

type LayoutButtonOptsProps = {
  layoutID: number;
  changeGridLayout: () => void;
};

export const layoutButtonOpts = ({
  layoutID,
  changeGridLayout,
}: LayoutButtonOptsProps) => {
  return {
    // name: 'layoutButton',
    location: "center",
    widget: "dxButton",
    options: {
      elementAttr: { id: "grdLayoutButton" },
      id: "grdLayoutButton",
      icon: layoutID === 1 ? "doc" : layoutID === 2 ? "fullscreen" : "file",
      hint: "Formatul vizualizarii: Portret | Vedere | Ecran intreg",
      onClick: changeGridLayout,
    },
  };
};

type InitialStateButtonOptsProps = {
  resetToInitialState: () => void;
};

export const initialSateButtonOpts = ({
  resetToInitialState,
}: InitialStateButtonOptsProps) => {
  return {
    name: "initialStateButton",
    location: "center",
    widget: "dxButton",
    options: {
      elementAttr: { id: "grdInitialStateButton" },
      id: "grdInitialStateButton",
      icon: "home",
      hint: "Revenire la starea initiala",
      onClick: resetToInitialState,
    },
  };
};
type ExportButtonOptsProps = {
  exportGridToPdf: () => void;
};

export const exportButtonOpts = ({
  exportGridToPdf,
}: ExportButtonOptsProps) => {
  return {
    name: "exportButton",
    location: "center",
    widget: "dxButton",
    options: {
      id: "exportButton",
      icon: "exportpdf",
      hint: "Export în PDF",
      onClick: exportGridToPdf,
    },
  };
};
type ColumnChooserButtonOptsProps = {
  customShowColumnChooser: () => void;
};

export const columnChooserButtonOpts_pre23 = ({
  customShowColumnChooser,
}: ColumnChooserButtonOptsProps) => {
  return {
    name: "columnChooserButton",
    location: "center",
    widget: "dxButton",
    options: {
      hint: "Selecția coloanelor care vor fi afisate in tabel",
      onClick: customShowColumnChooser,
    },
  };
};

export const columnChooserButtonOpts = ({ customShowColumnChooser }) => {
  return {
    name: "columnChooserButton" as const,
    location: "center" as const,
    widget: "dxButton" as const,
    options: {
      hint: "Selecția coloanelor care vor fi afisate in tabel",
      onClick: customShowColumnChooser,
    },
  };
};

type SearchBoxOptsProps = {
  tsSearchBoxRef: React.RefObject<HTMLInputElement>;
  tsSrchBoxValue: string;
  tsSrchBoxValueChanged: (e: any) => void;
};

export const searchBoxOpts = ({
  tsSearchBoxRef,
  tsSrchBoxValue,
  tsSrchBoxValueChanged,
}: SearchBoxOptsProps) => {
  const commonTextEditOptions = {
    ...commonStyle({ elementclass: "filledunderline" }),
  };
  return {
    location: "after",
    visible: true,
    widget: "dxTextBox",
    options: {
      ...commonTextEditOptions,
      inputAttr: {
        id: "searchbox-random-id-971",
        ref: tsSearchBoxRef,
      },
      stylingMode: "underlined",
      width: 250,
      placeholder: "Cautare in tabel",
      value: tsSrchBoxValue,
      onValueChanged: tsSrchBoxValueChanged,
      valueChangeEvent: "keyup",
      showClearButton: true,
    },
  };
};

export const CurrencyPickerOpts = (
  items: TCurrency[],
  selectedCcy: string,
  onSelectionChanged: any
) => {
  return {
    location: "before",
    widget: "dxSelectBox",
    options: {
      id: "currencyselect",
      type: "text",
      elementAttr: {
        class: `toolbar-picker ${selectedCcy ? "" : "dx-state-invalid"}`,
      },
      width: 60,
      showClearButton: false,
      items: items,
      value: selectedCcy,
      onSelectionChanged: onSelectionChanged,
    },
  };
};

export const EmitentPickerOpts = (
  items: any[],
  selectedEmitent: string,
  onselectionchanged: any
) => {
  return {
    location: "before",
    widget: "dxSelectBox",
    options: {
      id: "emitentselect",
      type: "text",
      elementAttr: {
        class: `toolbar-picker ${selectedEmitent ? "" : "dx-state-invalid"}`,
      },
      invalidMessage: "Selectati emitentul",
      dataSource: items,
      displayExpr: "DENUMIRE",
      valueExpr: "CIF",
      value: selectedEmitent,
      onSelectionChanged: onselectionchanged,
    },
  };
};

type CustomGroupItemOptsProps = {
  name: string;
  column: string;
};
// customGroupItemOpts('totalHours', 'ORELUCR')
export const customGroupItemOpts = ({
  name,
  column,
}: CustomGroupItemOptsProps) => ({
  name,
  summaryType: "custom",
  showInColumn: column,
  alignByColumn: column,
  displayFormat: "{0}",
  showInGroupFooter: false,
});
// customTotalItemOpts('totalHours', 'ORELUCR')
export const customTotalItemOpts = ({
  name,
  column,
}: CustomGroupItemOptsProps) => ({
  name: name,
  summaryType: "custom",
  showInColumn: column,
  displayFormat: "{0}",
});
// total currency split
type GroupCustomItemProps = {
  name: string;
};

export const groupCustomItem = ({ name }: GroupCustomItemProps) => ({
  name,
  column: name,
  summaryType: "custom",
  showInColumn: name,
  alignByColumn: true,
  displayFormat: "{0}",
});

export const groupCustomItemConv = ({ name }: GroupCustomItemProps) => ({
  name,
  column: name,
  summaryType: "custom",
  showInColumn: name.replace("_CONV", ""),
  alignByColumn: true,
  displayFormat: "{0}",
});

type GroupCustomItemConversionProps = {
  name: string;
  currency: string;
};

// total currency split
type GroupCustomTotalsProps = {
  name: string;
  label?: string;
};

export const groupCustomTotals = ({ name, label }: GroupCustomTotalsProps) => ({
  name,
  column: name,
  summaryType: "custom",
  showInColumn: name,
  alignByColumn: name,
  displayFormat: label ? `${label}:{0}` : "{0}",
  showInGroupFooter: false,
});

export const groupCustomTotalsConv = ({
  name,
  label,
}: GroupCustomTotalsProps) => ({
  name,
  column: name,
  summaryType: "custom",
  showInColumn: name.replace("_CONV", ""),
  alignByColumn: name,
  displayFormat: label ? `${label}:{0}` : "{0}",
  showInGroupFooter: false,
});

export const totalsCount = ({ name, label }: GroupCustomTotalsProps) => ({
  name,
  column: name,
  summaryType: "count",
  showInColumn: name,
  alignByColumn: name,
  displayFormat: label ? `${label}:{0}` : "{0}",
  showInGroupFooter: false,
});

const toMinutes = (x: string): number =>
  compose(
    (a: string[]) => parseInt(a?.[0]) * 60 + parseInt(a?.[1]),
    split(":")
  )(x);

const toHoursMinutes = (mins: number): string => {
  const h: number = Math.floor(mins / 60);
  const m: number = mins % 60;
  const hh: string = h < 10 ? `0${h}` : h.toString();
  const mm: string = m < 10 ? `0${m}` : m.toString();
  return `${hh}:${mm}`;
};

export const calculateTotalHours = (options: any) => {
  if (options.name === "totalHours") {
    if (options.summaryProcess === "start") {
      options.totalValue = "0:00";
      options.totalMinutes = 0;
    } else if (options.summaryProcess === "calculate") {
      options.totalMinutes =
        options.totalMinutes + toMinutes(options.value.ORELUCR);
      options.totalValue = toHoursMinutes(options.totalMinutes);
    } else if (options.summaryProcess === "finalize") {
      const colNames = [];
      //
      for (let i = 0; i < options.component.columnCount(); i++) {
        if (options.component.columnOption(i, "groupIndex") > -1) {
          colNames.push(options.component.columnOption(i, "caption"));
        }
      }
      //
      options.totalValue =
        isNull(options.groupIndex) || isUndefined(options.groupIndex)
          ? options.totalValue
          : options.totalValue;
      // : `${lowerCase(colNames?.[options.groupIndex])}: ${options.totalValue}
      //   `;
    }
  }
};

export const calculateTotalValues = (options: any) => {
  // totals for groups for each currency
  if (
    [
      "VLAFCT_FTVA_TXT",
      "VALFCT_TVA_TXT",
      "VALFCT_CUTVA_TXT",
      "VALINC_TXT",
      "RESTINC_TXT",
      "VALFARATVACON_TXT",
      "VALTVACON_TXT",
      "VALFCON_TXT",
      "VALICON_TXT",
      "RESTICON_TXT",
      "VAL_TXT",
      "VAL_TVA_TXT",
      "TOTAL_TXT",
      "INCASAT_TXT",
      "REST_DE_INCASAT_TXT",
      "VAL_EUR_TXT",
      "VAL_TVA_EUR_TXT",
      "TOTAL_EUR_TXT",
      "INCASAT_EUR_TXT",
      "REST_DE_INCASAT_EUR_TXT",
      "VAL_TXT_EUR",
      "VAL_TXT_USD",
      "VAL_TXT_GBP",
      "VAL_TXT_CHF",
      "VAL_TXT_RON",
      "VAL_TXT_MDL",
      "VAL_TXT_CONV",
      "VAL_TVA_TXT_CONV",
      "TOTAL_TXT_CONV",
      "INCASAT_TXT_CONV",
      "REST_DE_INCASAT_TXT_CONV",
    ].includes(options.name)
  ) {
    /// accumulator, summ by currency //
    if (options.summaryProcess === "start") {
      options.totalValue = {
        CHF: 0,
        EUR: 0,
        GBP: 0,
        MDL: 0,
        RON: 0,
        USD: 0,
      };
    } else if (options.summaryProcess === "calculate") {
      try {
        const suma = (options.value || ("" as string)).split(" ");
        options.totalValue[suma[1]] =
          options.totalValue[suma[1]] + parseFloat(suma[0]);
      } catch (e) {
        console.log(e);
        console.log("options", options);
      }
    } else if (options.summaryProcess === "finalize") {
      const res = Object.entries(options.totalValue).reduce(
        (acc, [key, value]) => {
          return value
            ? `${acc === "0" ? "" : acc} ${(value as number).toFixed(
                2
              )} ${key} `
            : acc;
        },
        "0"
      );
      options.totalValue = `${res}`;
    }
  }
  //count distinct
  if (["CLIENT", "CTRDEN"].includes(options.name)) {
    if (options.summaryProcess === "start") {
      options.totalValue = [];
    } else if (options.summaryProcess === "calculate") {
      options.totalValue.push(options.value);
    } else if (options.summaryProcess === "finalize") {
      options.totalValue = uniq(options.totalValue).length;
    }
  }
};

export const calculateTotalValuesConv = (options: any) => {
  // totals for groups for each currency
  if (
    [
      "VLAFCT_FTVA_TXT",
      "VALFCT_TVA_TXT",
      "VALFCT_CUTVA_TXT",
      "VALINC_TXT",
      "RESTINC_TXT",
      "VALFARATVACON_TXT",
      "VALTVACON_TXT",
      "VALFCON_TXT",
      "VALICON_TXT",
      "RESTICON_TXT",
      "VAL_TXT",
      "VAL_TVA_TXT",
      "TOTAL_TXT",
      "INCASAT_TXT",
      "REST_DE_INCASAT_TXT",
      "VAL_EUR_TXT",
      "VAL_TVA_EUR_TXT",
      "TOTAL_EUR_TXT",
      "INCASAT_EUR_TXT",
      "REST_DE_INCASAT_EUR_TXT",
      "VAL_TXT_EUR",
      "VAL_TXT_USD",
      "VAL_TXT_GBP",
      "VAL_TXT_CHF",
      "VAL_TXT_RON",
      "VAL_TXT_MDL",
      "VAL_TXT_CONV",
      "VAL_TVA_TXT_CONV",
      "TOTAL_TXT_CONV",
      "INCASAT_TXT_CONV",
      "REST_DE_INCASAT_TXT_CONV",
    ].includes(options.name)
  ) {
    /// accumulator, summ by currency //
    if (options.summaryProcess === "start") {
      options.totalValue = {
        CHF: 0,
        EUR: 0,
        GBP: 0,
        MDL: 0,
        RON: 0,
        USD: 0,
      };
    } else if (options.summaryProcess === "calculate") {
      if (typeof options.value !== "string") {
        return;
      }
      /// if doesent have currency, safely ...
      if (options.value.split(" ").length !== 2) {
        return;
      }

      try {
        const suma = (options.value || ("" as string)).split(" ");
        options.totalValue[suma[1]] =
          options.totalValue[suma[1]] + parseFloat(suma[0]);
      } catch (e) {
        console.log(e);
        console.log("options", options);
      }
    } else if (options.summaryProcess === "finalize") {
      const res = Object.entries(options.totalValue).reduce(
        (acc, [key, value]) => {
          return value
            ? `${acc === "0" ? "" : acc} ${(value as number).toFixed(
                2
              )} ${key} `
            : acc;
        },
        "0"
      );
      options.totalValue = `${res}`;
    }
  }
  //count distinct
  if (["CLIENT", "CTRDEN"].includes(options.name)) {
    if (options.summaryProcess === "start") {
      options.totalValue = [];
    } else if (options.summaryProcess === "calculate") {
      options.totalValue.push(options.value);
    } else if (options.summaryProcess === "finalize") {
      options.totalValue = uniq(options.totalValue).length;
    }
  }
};

export const EmitentPickerOptsByCIF = (
  items: any[],
  selectedEmitent: string,
  onselectionchanged: any
) => {
  return {
    location: "before",
    widget: "dxSelectBox",
    options: {
      id: "emitentselect",
      type: "text",
      elementAttr: {
        class: `toolbar-picker ${selectedEmitent ? "" : "dx-state-invalid"}`,
      },
      invalidMessage: "Selectati emitentul",
      dataSource: items,
      displayExpr: "DENUMIRE",
      valueExpr: "CIF",
      value: selectedEmitent,
      onSelectionChanged: onselectionchanged,
    },
  };
};

export const exportToPdfOpts = ({ exportGridToPdf }) => {
  return {
    // name: 'exportButton',
    location: "center" as const,
    widget: "dxButton" as const,
    options: {
      id: "exportButton",
      icon: "exportpdf",
      hint: "Export în PDF",
      onClick: exportGridToPdf,
    },
  };
};
